/* The primitives every screen is built from.
 *
 * These are deliberately thin. The design system lives in `ui.module.css`, and
 * these components exist for two reasons only: to give the vocabulary a name
 * that TypeScript can check, and to make the one rule that matters — a status is
 * a glyph, a word and a colour, never a colour alone — impossible to break by
 * taking a shortcut in a screen.
 */

import { useState, type CSSProperties, type ReactNode } from 'react';
import css from './ui.module.css';
import type { Status } from '../theme/tokens';

/* ============================================================ structure */

export function Stack({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return <div className={wide ? css.stackLg : css.stack}>{children}</div>;
}

export function Row({ children }: { children: ReactNode }) {
  return <div className={css.row}>{children}</div>;
}

export function Split({ children }: { children: ReactNode }) {
  return <div className={css.split}>{children}</div>;
}

/* ============================================================ card */

export function Card({
  children,
  hero,
  flat,
  style,
}: {
  children: ReactNode;
  hero?: boolean;
  flat?: boolean;
  style?: CSSProperties;
}) {
  const cls = flat ? css.cardFlat : hero ? css.cardHero : css.card;
  return (
    <div className={cls} style={style}>
      {children}
    </div>
  );
}

/** A card header. `title` is the card's name, `sub` the one line that says what
 *  it is for — the prototype never shows a title without one. */
export function CardHead({
  title,
  sub,
  right,
  big,
  children,
}: {
  title?: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  big?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className={big ? css.cardHeadBig : css.cardHead}>
      <div className={css.grow}>
        {title ? <div className={big ? css.cardTitleLg : css.cardTitle}>{title}</div> : null}
        {sub ? <div className={css.cardSub}>{sub}</div> : null}
        {children}
      </div>
      {right}
    </div>
  );
}

export function CardBody({ children }: { children: ReactNode }) {
  return <div className={css.cardBody}>{children}</div>;
}

export function CardFoot({ children }: { children: ReactNode }) {
  return <div className={css.cardFoot}>{children}</div>;
}

/* ============================================================ KPI */

export function KpiGrid({ children, large }: { children: ReactNode; large?: boolean }) {
  return <div className={large ? css.kpiGridLg : css.kpiGrid}>{children}</div>;
}

export function Kpi({
  label,
  value,
  note,
  color,
  tone,
  onClick,
  icon,
  iconFg,
  iconBg,
  tall,
}: {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
  color?: string;
  /** The selected state of a KPI used as a filter. */
  tone?: { bg: string; bd: string; c: string };
  onClick?: () => void;
  icon?: ReactNode;
  iconFg?: string;
  iconBg?: string;
  tall?: boolean;
}) {
  const inner = (
    <>
      {icon ? (
        <span className={css.kpiIcon} style={{ background: iconBg, color: iconFg }}>
          {icon}
        </span>
      ) : null}
      <span className={css.kpiFacts}>
        <span className={tall ? css.kpiLabelTight : css.kpiLabel} style={tone ? { color: tone.c } : undefined}>
          {label}
        </span>
        <span className={tall ? css.kpiValueLg : css.kpiValue} style={{ color: color ?? tone?.c }}>
          {value}
        </span>
        {note ? <span className={css.kpiNote}>{note}</span> : null}
      </span>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${tall ? css.kpiTall : css.kpi} ${css.kpiBtn}`}
        style={tone ? { background: tone.bg, border: `1.5px solid ${tone.bd}` } : undefined}
      >
        {inner}
      </button>
    );
  }
  return <div className={tall ? css.kpiTall : css.kpi}>{inner}</div>;
}

/* ============================================================ status */

/** The only way to render a status. It takes a whole `Status`, so a caller
 *  cannot pass a bare colour and lose the word. */
export function StatusChip({
  status,
  small,
  title,
}: {
  status: Status;
  small?: boolean;
  title?: string;
}) {
  return (
    <span
      className={small ? `${css.status} ${css.statusSm}` : css.status}
      style={{ color: status.fg, background: status.bg }}
      title={title}
    >
      <span aria-hidden="true">{status.glyph}</span>
      {status.label}
    </span>
  );
}

export function SeverityBadge({ status }: { status: Status }) {
  return (
    <span className={css.sev} style={{ color: status.fg, background: status.bg }}>
      <span
        aria-hidden="true"
        style={{ width: 7, height: 7, borderRadius: '50%', background: status.fg, flex: 'none' }}
      />
      {status.label}
    </span>
  );
}

export function Tag({ children, fg, bg }: { children: ReactNode; fg: string; bg: string }) {
  return (
    <span className={css.tag} style={{ color: fg, background: bg }}>
      {children}
    </span>
  );
}

/** ASAP marks a booking placed for "now" rather than a scheduled slot. It is
 *  `is_immediate`, not a clinical judgement — every ambulance booking is
 *  immediate — so it is neutral, and the bookings screen shows it only while the
 *  booking is still live. A real emergency is a worker's SOS, not this badge. */
export function Asap() {
  return <span className={css.asap}>ASAP</span>;
}

export function Pill({ children, teal }: { children: ReactNode; teal?: boolean }) {
  return (
    <span className={teal ? `${css.pill} ${css.pillOn}` : css.pill}>
      {!teal ? <span className={css.pillDot} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/* ============================================================ controls */

export function Btn({
  children,
  onClick,
  variant = 'plain',
  size,
  disabled,
  block,
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'plain' | 'primary' | 'ok' | 'danger';
  size?: 'xs' | 'sm' | 'lg' | 'block';
  disabled?: boolean;
  block?: boolean;
  title?: string;
}) {
  const parts = [css.btn];
  if (size === 'xs') parts.push(css.btnXs);
  if (size === 'sm') parts.push(css.btnSm);
  if (size === 'lg') parts.push(css.btnLg);
  if (variant === 'primary') parts.push(css.btnPrimary);
  if (variant === 'ok') parts.push(css.btnOk);
  if (variant === 'danger') parts.push(css.btnDanger);
  if (block) parts.push(css.btnBlock);
  return (
    <button type="button" className={parts.join(' ')} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
  );
}

/** A filter chip. `on` is the selected state. */
export function Chip({
  children,
  on,
  onClick,
  small,
}: {
  children: ReactNode;
  on?: boolean;
  onClick?: () => void;
  small?: boolean;
}) {
  const parts = [css.chip];
  if (small) parts.push(css.chipSm);
  if (on) parts.push(css.chipOn);
  return (
    <button type="button" className={parts.join(' ')} onClick={onClick} aria-pressed={on}>
      {children}
    </button>
  );
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
}: {
  items: readonly { key: T; label: string }[];
  value: T;
  onChange: (key: T) => void;
}) {
  return (
    <div className={css.tabs} role="tablist">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          role="tab"
          aria-selected={it.key === value}
          className={`${css.tab} ${it.key === value ? css.tabOn : ''}`}
          onClick={() => onChange(it.key)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className={`${css.toggle} ${on ? css.toggleOn : css.toggleOff}`}
      onClick={onClick}
      role="switch"
      aria-checked={on}
      aria-label={label}
    >
      <span className={css.knob} />
    </button>
  );
}

/* ============================================================ fields */

export function Field({
  label,
  children,
  style,
}: {
  label: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <label className={css.field} style={style}>
      {label}
      {children}
    </label>
  );
}

export function Input({
  value,
  onChange,
  placeholder,
  size,
  type,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  size?: 'sm' | 'lg';
  type?: string;
  ariaLabel?: string;
}) {
  const parts = [css.input];
  if (size === 'sm') parts.push(css.inputSm);
  if (size === 'lg') parts.push(css.inputLg);
  return (
    <input
      className={parts.join(' ')}
      value={value}
      type={type ?? 'text'}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** A password field with Show / Hide (2026-10-29), so a person can check what
 *  they typed. It starts hidden and hides again when the form goes away. */
export function PasswordInput({
  value,
  onChange,
  size,
  ariaLabel,
  autoComplete,
}: {
  value: string;
  onChange: (v: string) => void;
  size?: 'sm' | 'lg';
  ariaLabel?: string;
  /** "current-password" on a sign-in, "new-password" where one is chosen. */
  autoComplete?: string;
}) {
  const [shown, setShown] = useState(false);
  const parts = [css.input, css.pwInput];
  if (size === 'sm') parts.push(css.inputSm);
  if (size === 'lg') parts.push(css.inputLg);
  return (
    <div className={css.pwWrap}>
      <input
        className={parts.join(' ')}
        value={value}
        type={shown ? 'text' : 'password'}
        aria-label={ariaLabel}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className={css.pwToggle}
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        onClick={() => setShown((s) => !s)}
      >
        {shown ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

export function Textarea({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      className={css.textarea}
      value={value}
      rows={rows}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** A value the operator is locked out of, with the reason beside it. The lock
 *  glyph is part of the design: it says "this is not disabled by a bug". */
export function Locked({ children }: { children: ReactNode }) {
  return (
    <div className={css.locked}>
      <span aria-hidden="true">{'\uD83D\uDD12'}</span>
      {children}
    </div>
  );
}

export function Note({
  children,
  tone = 'info',
}: {
  children: ReactNode;
  tone?: 'info' | 'amber' | 'danger' | 'teal' | 'ok';
}) {
  const map = {
    info: css.noteInfo,
    amber: css.noteAmber,
    danger: css.noteDanger,
    teal: css.noteTeal,
    ok: css.noteOk,
  } as const;
  return <div className={`${css.note} ${map[tone]}`}>{children}</div>;
}

/* ============================================================ bar */

export function Bar({
  value,
  target,
  color,
  width,
}: {
  value: number;
  /** The target mark, as a percentage. The spec's capacity view always shows
   *  the number against its target, never the number alone. */
  target?: number;
  color: string;
  width: string;
}) {
  return (
    <div className={css.barTrack}>
      <span className={css.barFill} style={{ width, background: color }} />
      {target !== undefined ? <span className={css.barMark} style={{ left: `${target}%` }} /> : null}
      <span className="sr-only">{`${value}`}</span>
    </div>
  );
}

/* ============================================================ avatar */

export function Avatar({
  initials,
  fg,
  bg,
  large,
  dot,
}: {
  initials: string;
  fg: string;
  bg: string;
  large?: boolean;
  dot?: string;
}) {
  const face = (
    <span className={large ? css.avatarLg : css.avatar} style={{ background: bg, color: fg }} aria-hidden="true">
      {initials}
    </span>
  );
  if (!dot) return face;
  return (
    <span className={css.avatarWrap}>
      {face}
      <span className={css.avatarDot} style={{ background: dot }} />
    </span>
  );
}

/* ============================================================ empty */

export function Empty({ children, big }: { children: ReactNode; big?: boolean }) {
  return <div className={big ? css.emptyBig : css.empty}>{children}</div>;
}

export function AllClear({ title, sub }: { title: ReactNode; sub: ReactNode }) {
  return (
    <div className={css.emptyBig}>
      <div className={css.emptyIcon} aria-hidden="true">
        {'\u2713'}
      </div>
      <div style={{ font: '700 16px var(--font-display)', marginTop: 12 }}>{title}</div>
      <div className={css.sub} style={{ marginTop: 4 }}>
        {sub}
      </div>
    </div>
  );
}

/* ============================================================ icons */

const PATHS = {
  close: 'M6 6l12 12M18 6 6 18',
  bell: 'M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6 M10 19a2 2 0 0 0 4 0',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z M21 21l-4.35-4.35',
  pin: 'M12 21s-7-6.1-7-11a7 7 0 1 1 14 0c0 4.9-7 11-7 11z',
  phone: 'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z',
  chevron: 'M9 18l6-6-6-6',
  plus: 'M12 5v14M5 12h14',
  check: 'M20 6L9 17l-5-5',
  alert: 'M12 9v4 M12 17h.01 M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  /* ---- domain glyphs ---- */
  van: 'M1 6h13v9H1z M14 9h4l3 3v3h-7z M5.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M17.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
  heart:
    'M20.8 5.6a5 5 0 0 0-7.1 0L12 7.3l-1.7-1.7a5 5 0 1 0-7.1 7.1l8.8 8.8 8.8-8.8a5 5 0 0 0 0-7.1z',
  box: 'M21 8l-9-5-9 5 9 5 9-5z M3 8v8l9 5 9-5V8 M12 13v8',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2',
  rupee: 'M6 3h12 M6 8h12 M6 13h5a4 4 0 0 0 0-8 M6 13l7 8',
  users:
    'M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M22 21v-2a4 4 0 0 0-3-3.9 M16 3.1a4 4 0 0 1 0 7.8',
  bolt: 'M13 2 4 14h7l-1 8 9-12h-7l1-8z',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  chart: 'M3 3v18h18 M7 15l4-4 3 3 5-6',
  flag: 'M4 22V4 M4 4h12l-2 4 2 4H4',
  route: 'M6 19a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 13V9a4 4 0 0 1 4-4h5',
  hospital: 'M12 7v6 M9 10h6 M4 21V7l8-4 8 4v14z',
  map: 'M1 6l7-3 8 3 7-3v15l-7 3-8-3-7 3z M8 3v15 M16 6v15',
  key: 'M15.5 8.5a3.5 3.5 0 1 1-3.5-3.5 3.5 3.5 0 0 1 3.5 3.5z M13.2 11.3 4 20.5V22h3v-2h2v-2h2l2.2-2.2',
  book: 'M4 4h11a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3z M4 17a3 3 0 0 1 3-3h11',
  chevronDown: 'M6 9l6 6 6-6',
  /* ---- the rail's own glyphs ----
   * These thirteen paths are copied verbatim from the approved prototype's
   * `ICON` map, one per module, and `nav.ts` points every module at its own.
   * They are here rather than reusing a similar-looking glyph because the rail
   * is SCANNED, not read: an operator finds "Dispatch" by its shape, and a
   * substituted icon moves the target. */
  board: 'M4 13h6V4H4z M14 20h6v-9h-6z M14 4h6v4h-6z M4 20h6v-3H4z',
  doc: 'M8 4h8 M6 8h12v12H6z M9 12h6 M9 16h4',
  dispatchList: 'M4 6h16 M4 12h10 M4 18h7 M16 15l3 3-3 3',
  bars: 'M4 20V10 M10 20V4 M16 20v-7 M22 20H2',
  shieldAlert: 'M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3z M12 8v5 M12 16v.01',
  people:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z M2 20v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1 M17 4.2a3.5 3.5 0 0 1 0 6.6 M22 20v-1a5 5 0 0 0-3.5-4.8',
  shieldCheck: 'M9 12l2 2 4-4 M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z',
  tag: 'M20 12 12 20l-8-8V4h8l8 8z M8 8h.01',
  banknote: 'M3 7h18v10H3z M12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  headset: 'M4 14v-2a8 8 0 0 1 16 0v2 M4 14h3v5H4z M17 14h3v5h-3z',
  log: 'M5 4h14v16H5z M9 8h6 M9 12h6 M9 16h3',
  sliders: 'M4 6h10 M18 6h2 M4 12h4 M12 12h8 M4 18h12 M16 4v4 M10 10v4 M16 16v4',
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 17 }: { name: IconName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name]
        .split(' M')
        .map((d, i) => <path key={i} d={i === 0 ? d : `M${d}`} />)}
    </svg>
  );
}
